import React, { useEffect } from "react";
import { X, ExternalLink } from "lucide-react";
import { usePortfolio } from "../providers/PortfolioContext";
import { RippleButton } from "../components/RippleButton";
import { CustomCursor } from "../components/CustomCursor";
import { useCursor } from "../providers/CursorContext";
import { ThemeToggle } from "../components/ThemeToggle";

import icCircleWhite from "../assets/images/ic_circle_white.webp";

interface DetailPortfolioScreenProps {
  onNavigate: (route: string) => void;
}

export const DetailPortfolioScreen: React.FC<DetailPortfolioScreenProps> = ({
  onNavigate,
}) => {
  const { portfolios } = usePortfolio();
  const { setCursorSize, setHover } = useCursor();

  // Extract ID from query parameters e.g., ?id=1
  const queryParams = new URLSearchParams(window.location.search);
  const portfolioId = queryParams.get("id");

  const portfolio = portfolios.find((p) => p.id === portfolioId);

  // Parse tags by combining platform and technology
  const tags = portfolio
    ? [
        ...portfolio.platform.split(",").map((t) => t.trim()),
        ...portfolio.technology.split(",").map((t) => t.trim()),
      ]
    : [];

  const handleGoToApp = () => {
    if (portfolio?.url) {
      window.open(portfolio.url, "_blank");
    }
  };

  // Reset hover state when unmounting to avoid cursor stuck in hover state
  useEffect(() => {
    return () => {
      setHover(false);
      setCursorSize(24);
    };
  }, []);

  if (!portfolio) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col bg-bg transition-colors duration-300">
        <h2 className="text-2xl font-bold font-rubik text-dark mb-4">
          Portfolio Not Found
        </h2>
        <RippleButton text="Go Back" onTap={() => onNavigate("/portfolios")} />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-bg transition-colors duration-300 flex flex-col md:flex-row relative overflow-hidden select-none">
      
      {/* 1. Image Panel (Left side on desktop, top side on mobile) */}
      <div className="w-full md:w-[40%] h-[40vh] md:h-screen bg-primary relative flex items-center justify-center z-10 shrink-0">
        
        {/* Floating white circles/particles background */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-0 opacity-35">
          {[...Array(10)].map((_, i) => {
            const size = 10 + (i % 3) * 10;
            const left = 10 + (i * 13) % 80;
            const top = 10 + (i * 17) % 80;
            const animClass = `animate-particle-${(i % 5) + 1}`;
            return (
              <img
                key={i}
                src={icCircleWhite}
                alt=""
                className={`absolute object-contain pointer-events-none ${animClass}`}
                style={{
                  width: size,
                  height: size,
                  left: `${left}%`,
                  top: `${top}%`,
                }}
              />
            );
          })}
        </div>

        {/* Action Buttons Top Left */}
        <div className="absolute top-4 left-4 z-25 flex items-center gap-2">
          <ThemeToggle textColor="text-white" hoverColor="bg-white/10" />
          <RippleButton
            color="bg-transparent"
            hoverColor="bg-white/10"
            padding="p-3"
            radius="rounded-full"
            onTap={() => onNavigate("/portfolios")}
          >
            <X className="w-8 h-8 text-white" />
          </RippleButton>
        </div>

        {/* Portfolio Thumbnail Frame */}
        <div className="z-10 bg-white border-2 border-white rounded-[24px] overflow-hidden shadow-2xl p-0.5 w-[60%] max-w-[280px] md:max-w-xs aspect-[3/4.2] transform transition-transform hover:scale-[1.02] duration-300">
          <img
            src={portfolio.image}
            alt={portfolio.title}
            className="w-full h-full object-cover rounded-[20px]"
          />
        </div>
      </div>

      {/* 2. Scrollable Details Panel (Right side on desktop, bottom side on mobile) */}
      <div className="flex-1 h-[60vh] md:h-screen overflow-y-auto px-6 py-8 md:px-16 md:py-20 flex flex-col justify-start relative select-text">
        <div className="max-w-3xl w-full mx-auto">
          {/* Header Row: Title & Go To Apps link */}
          <div className="flex items-center justify-between gap-6 mb-4 animate-[slideUp_0.5s_ease-out_both] animation-delay-600">
            <h1 className="font-rubik font-bold text-3xl md:text-5xl text-dark">
              {portfolio.title}
            </h1>
            {portfolio.url && (
              <RippleButton
                onTap={handleGoToApp}
                padding="px-5 py-2.5"
                color="bg-primary"
                className="shrink-0"
              >
                <div className="flex items-center gap-2">
                  <span className="font-rubik text-xs md:text-sm font-bold text-white dark:text-neutral-900">Go to Apps</span>
                  <ExternalLink className="w-4 h-4 text-white dark:text-neutral-900" />
                </div>
              </RippleButton>
            )}
          </div>

          {/* Yellow Divider Accent */}
          <div className="w-24 h-1.5 bg-primary mb-6 rounded-full animate-[slideUp_0.5s_ease-out_both]" style={{ animationDelay: '150ms' }} />

          {/* Platform & Tech Tags Row */}
          <div className="flex flex-wrap items-center gap-2 mb-8 text-dark/70 font-rubik text-sm md:text-base animate-[slideUp_0.5s_ease-out_both]" style={{ animationDelay: '250ms' }}>
            {tags.map((tag, idx) => (
              <span key={idx} className="flex items-center">
                {idx > 0 && <span className="mx-2.5 text-gray-300 select-none">•</span>}
                <span className="font-medium">{tag}</span>
              </span>
            ))}
          </div>

          {/* Description Content */}
          <p className="font-rubik text-sm md:text-base text-dark/80 leading-relaxed whitespace-pre-line animate-[slideUp_0.5s_ease-out_both]" style={{ animationDelay: '350ms' }}>
            {portfolio.description}
          </p>
        </div>
      </div>

      {/* 3. Custom Cursor Layer */}
      <CustomCursor />
    </div>
  );
};
